import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, ThemeContext, CartContext } from '../App';
import { ShoppingCart, User, Menu, X, Sun, Moon, Search, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_58aca9c2-2cb6-4ff9-a615-17272856b112/artifacts/wb3ejkz9_Logo%20-%20Transparent.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header() {
  const { user, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { cart } = useContext(CartContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const [categories, setCategories] = useState([]);
  const [navLinks, setNavLinks] = useState([
    { id: 'products', label: 'Products', path: '/products', type: 'dropdown', enabled: true },
    { id: 'vendors', label: 'Vendors', path: '/vendors', type: 'link', enabled: true },
    { id: 'projects', label: 'Projects', path: '/projects', type: 'link', enabled: true },
    { id: 'about', label: 'About', path: '/about', type: 'link', enabled: true },
    { id: 'contact', label: 'Contact', path: '/contact', type: 'link', enabled: true },
  ]);
  const [navLoaded, setNavLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchNavigation();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchNavigation = async () => {
    try {
      const res = await axios.get(`${API}/navigation`);
      setNavLinks(res.data.links || []);
    } catch {
      // Keep defaults
    } finally {
      setNavLoaded(true);
    }
  };

  const enabledLinks = navLinks.filter(l => l.enabled);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim() || value.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/products?search=${encodeURIComponent(value)}&limit=6`);
        setSearchResults(res.data.products || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 250);
  };

  const selectProduct = (slug) => {
    navigate(`/products/${slug}`);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Close search on click outside
  React.useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <header className="bg-[#0A0A0A]/95 backdrop-blur-md border-b border-zinc-800 sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Tech Distribution" className="h-8 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {enabledLinks.map(link => {
              if (link.type === 'dropdown' && link.id === 'products') {
                return (
                  <DropdownMenu key={link.id}>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center text-zinc-300 hover:text-[#78BE20] transition-colors font-medium" data-testid="products-dropdown">
                        {link.label} <ChevronDown className="ml-1 h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-zinc-900 border-zinc-800 w-56">
                      <DropdownMenuItem asChild>
                        <Link to="/products" className="cursor-pointer text-zinc-300 hover:text-[#78BE20] focus:text-[#78BE20] focus:bg-zinc-800">
                          All Products
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      {categories.map(cat => (
                        <DropdownMenuItem key={cat.slug} asChild>
                          <Link to={`/category/${cat.slug}`} className="cursor-pointer text-zinc-300 hover:text-[#78BE20] focus:text-[#78BE20] focus:bg-zinc-800">
                            {cat.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              return (
                <Link key={link.id} to={link.path} className="text-zinc-300 hover:text-[#78BE20] transition-colors font-medium" data-testid={`${link.id}-link`}>
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-zinc-400 hover:text-[#78BE20] transition-colors"
              data-testid="search-toggle"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-zinc-400 hover:text-[#78BE20] transition-colors"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* Cart */}
            {user && (
              <Link to="/cart" className="p-2 text-zinc-400 hover:text-[#78BE20] transition-colors relative" data-testid="cart-link">
                <ShoppingCart className="h-5 w-5" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#78BE20] text-black text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Link>
            )}

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 text-zinc-400 hover:text-[#78BE20] transition-colors" data-testid="user-menu">
                    <User className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800 w-48">
                  <div className="px-3 py-2 text-sm text-zinc-400">
                    {user.first_name} {user.last_name}
                    {user.account_tier !== 'retail' && (
                      <span className="ml-2 px-2 py-0.5 bg-[#78BE20]/20 text-[#78BE20] text-xs rounded uppercase">
                        {user.account_tier}
                      </span>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer text-zinc-300 hover:text-[#78BE20] focus:text-[#78BE20] focus:bg-zinc-800">
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer text-zinc-300 hover:text-[#78BE20] focus:text-[#78BE20] focus:bg-zinc-800">
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-zinc-300 hover:text-red-500 focus:text-red-500 focus:bg-zinc-800">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-[#78BE20]" data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="register-btn">
                    Register
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-zinc-400 hover:text-[#78BE20] transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="py-4 border-t border-zinc-800 relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  placeholder="Search products..."
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-4 py-2 text-white placeholder:text-zinc-500 focus:border-[#78BE20] focus:outline-none"
                  autoFocus
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button type="button" onClick={() => { setSearchQuery(''); setSearchResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs">
                    <X className="h-4 w-4" />
                  </button>
                )}
                {/* Live Results Dropdown */}
                {searchQuery.length >= 2 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-2xl max-h-[400px] overflow-y-auto" data-testid="search-results-dropdown">
                    {searchLoading ? (
                      <div className="px-4 py-3 text-zinc-500 text-sm">Searching...</div>
                    ) : searchResults.length > 0 ? (
                      <>
                        <div className="px-3 py-1.5 text-[10px] uppercase text-zinc-500 border-b border-zinc-800">{searchResults.length} product{searchResults.length !== 1 ? 's' : ''} found</div>
                        {searchResults.map(p => (
                          <div
                            key={p.id}
                            className="px-3 py-2.5 hover:bg-zinc-800 cursor-pointer flex items-center gap-3 border-b border-zinc-800/50 last:border-0 transition-colors"
                            onMouseDown={() => selectProduct(p.slug)}
                            data-testid={`search-result-${p.slug}`}
                          >
                            {p.images?.[0] ? (
                              <img src={p.images[0]} alt="" className="h-10 w-10 rounded object-cover bg-zinc-800 flex-shrink-0" />
                            ) : (
                              <div className="h-10 w-10 rounded bg-zinc-800 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{p.name}</p>
                              <p className="text-zinc-500 text-xs">{p.sku} {p.category && `· ${p.category}`}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-[#78BE20] font-bold text-sm">${(p.retail_price || p.price || 0).toFixed(2)}</p>
                              {p.stock_qty !== undefined && p.stock_qty <= 0 && (
                                <span className="text-[10px] text-red-400">Out of stock</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {searchResults.length >= 6 && (
                          <div
                            className="px-3 py-2.5 text-center text-sm text-[#78BE20] hover:bg-zinc-800 cursor-pointer"
                            onMouseDown={() => handleSearch({ preventDefault: () => {} })}
                          >
                            View all results for "{searchQuery}"
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-3 text-zinc-500 text-sm">No products found for "{searchQuery}"</div>
                    )}
                  </div>
                )}
              </div>
              <Button type="submit" className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="search-submit">
                Search
              </Button>
            </form>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-zinc-900 border-t border-zinc-800">
          <div className="px-4 py-4 space-y-4">
            {enabledLinks.map(link => {
              if (link.type === 'dropdown' && link.id === 'products') {
                return (
                  <React.Fragment key={link.id}>
                    <Link to="/products" className="block text-zinc-300 hover:text-[#78BE20] py-2" onClick={() => setMobileMenuOpen(false)}>
                      All Products
                    </Link>
                    {categories.map(cat => (
                      <Link key={cat.slug} to={`/category/${cat.slug}`} className="block text-zinc-400 hover:text-[#78BE20] py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                        {cat.name}
                      </Link>
                    ))}
                  </React.Fragment>
                );
              }
              return (
                <Link key={link.id} to={link.path} className="block text-zinc-300 hover:text-[#78BE20] py-2" onClick={() => setMobileMenuOpen(false)}>
                  {link.label}
                </Link>
              );
            })}
            {!user && (
              <div className="pt-4 border-t border-zinc-800 space-y-2">
                <Link to="/login" className="block" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-zinc-700 text-zinc-300">Login</Button>
                </Link>
                <Link to="/register" className="block" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b]">Register</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
